<template>
  <div v-if="hasBookingIdOrInvoices" class="mb-4 mt-6 space-y-4 rounded-md bg-white p-4">
    <div class="grid gap-4 pb-4 md:grid-cols-2">
      <InputField
        name="amount"
        :label="t('amount')"
        type="number"
        :max-fraction-digits="2"
        :min="0"
        adornment="€"
      />
      <SelectField name="direction" :label="t('direction')" :options="directions" />
      <SelectField name="paymentType" :label="t('paymentType')" :options="paymentTypes" />
      <DatepickerField name="date" :label="t('paymentDate')" />
      <InputField name="referenceId" :label="t('referenceId')" />
      <SelectField
        v-if="selectableInvoices?.length"
        name="invoiceId"
        :label="t('invoice')"
        :options="invoicesOptions"
        empty-value-to="null"
      />
      <SelectField
        v-if="hasAllocationScope"
        name="allocationMode"
        :label="t('allocationMode')"
        :options="allocationModeOptions"
      />
      <SelectField
        v-if="hasAllocationScope && needsCounterpartyType"
        name="counterpartyType"
        :label="t('counterpartyType.label')"
        :options="counterpartyTypeOptions"
        empty-value-to="null"
      />
    </div>
    <LedgerEntrySelector
      v-if="allocationMode === 'manual' && hasAllocationScope && ledgerEntryScope"
      v-model="selectedLedgerEntries"
      :scope="ledgerEntryScope"
      amount-to-select-key="amountToPay"
    />
    <div class="flex justify-end gap-2">
      <CVButton variant="default" @click.prevent="savePayment">
        {{ t('savePayment') }}
      </CVButton>
      <CVButton @click.prevent="$emit('cancel')">
        {{ t('cancel') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Invoice } from '@/entities/invoices/invoice.entity';
import { CounterpartyType } from '@/entities/ledger/ledger-entry.entity';
import { PaymentStatus } from '@/entities/payments/payment-status.enum';
import { PaymentType } from '@/entities/payments/payment-type.enum';
import type { CreatePaymentDto } from '@/entities/payments/payment.entity';
import { useI18n } from 'vue-i18n';
import { paymentCreateSchema } from '@/validation/payment.schema';
import { useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import {
  type BookingScopeDto,
  type InvoiceScopeDto,
  type LedgerEntriesScopeDto,
} from '@carvia/ros-client-types';
import DatepickerField from '../DatepickerField.vue';
import InputField from '../InputField.vue';
import SelectField from '../SelectField.vue';
import LedgerEntrySelector, {
  type SelectedLedgerEntry,
} from '../ledger-entries/LedgerEntrySelector.vue';
import type { SelectOption } from '@/components/Select.vue';
import { useCounterpartyTypeOptions } from '@/hooks/use-counterparty-type-options';

export type PaymentFormSubmitPayload = CreatePaymentDto & {
  allocation?: { scope: BookingScopeDto | InvoiceScopeDto | LedgerEntriesScopeDto };
};

const props = defineProps<{
  bookingId?: string;
  initialInvoiceId?: string;
  initialDirection?: 'payment' | 'credit';
  selectableInvoices?: Invoice[];
}>();

const emit = defineEmits<{
  (e: 'submit', value: PaymentFormSubmitPayload): void;
  (e: 'cancel'): void;
}>();

const { t } = useI18n();

const hasBookingIdOrInvoices = computed(
  () => !!(props.bookingId || props.initialInvoiceId || props.selectableInvoices?.length),
);

const hasAllocationScope = computed(() => !!(props.bookingId || props.initialInvoiceId));

const needsCounterpartyType = computed(() => props.bookingId && !values.invoiceId);

const { handleSubmit, values } = useForm({
  initialValues: getNewPayment(),
  validationSchema: paymentCreateSchema,
});

const allocationMode = computed(() => values.allocationMode);
const selectedLedgerEntries = ref<SelectedLedgerEntry[]>([]);

watch([allocationMode, () => values.counterpartyType, () => values.invoiceId], () => {
  selectedLedgerEntries.value = [];
});

const ledgerEntryScope = computed(() => {
  if (!hasAllocationScope.value) return undefined;
  const invoiceId = values.invoiceId;
  if (invoiceId) {
    return { invoiceId };
  }
  if (props.bookingId) {
    return {
      bookingId: props.bookingId,
      counterpartyType: (values.counterpartyType as CounterpartyType) ?? CounterpartyType.CUSTOMER,
    };
  }
  return undefined;
});

const savePayment = handleSubmit(async (values) => {
  if (!hasBookingIdOrInvoices.value) return;
  const newPayment: PaymentFormSubmitPayload = {
    status: PaymentStatus.PAID,
    bookingId: props.bookingId ?? undefined,
    invoiceId: values.invoiceId ?? undefined,
    amount: Math.abs(Number(values.amount)) * Number(values.direction),
    paymentType: values.paymentType ?? PaymentType.VISA,
    referenceId: values.referenceId ?? undefined,
    date: values.date,
    counterpartyType: values.counterpartyType ?? undefined,
  };

  if (hasAllocationScope.value) {
    const mode = values.allocationMode as 'none' | 'auto' | 'manual';
    if (mode === 'manual' && selectedLedgerEntries.value.length > 0) {
      newPayment.allocation = {
        scope: {
          type: 'ledgerEntries',
          ledgerEntries: selectedLedgerEntries.value,
        },
      };
    } else if (mode === 'auto') {
      const invoiceId = values.invoiceId;
      if (invoiceId) {
        newPayment.allocation = { scope: { type: 'invoice', invoiceId } };
      } else if (props.bookingId) {
        newPayment.allocation = {
          scope: {
            type: 'booking',
            bookingId: props.bookingId,
            counterpartyType:
              (values.counterpartyType as CounterpartyType) ?? CounterpartyType.CUSTOMER,
          },
        };
      }
    }
  }

  emit('submit', newPayment);
});

const paymentTypes = ref(
  Object.entries(PaymentType).map(([key, value]) => ({
    label: t(`paymentTypes.${key}`),
    value,
  })),
);

const allocationModeOptions = computed<SelectOption[]>(() => [
  { label: t('dontAutoAllocate'), value: 'none' },
  { label: t('allocateAll'), value: 'auto' },
  { label: t('selectManual'), value: 'manual' },
]);

const counterpartyTypeOptions = useCounterpartyTypeOptions();

const invoicesOptions = computed(() => [
  { label: t('noInvoice'), value: null },
  ...(props.selectableInvoices?.map((invoice) => ({
    label: `${invoice.completeInvoiceNumber ?? t('iNrNA')} - ${invoice.title ?? ''}`,
    value: invoice.id,
  })) ?? []),
]);

function getNewPayment() {
  return {
    invoiceId: props.initialInvoiceId ?? null,
    direction: props.initialDirection === 'credit' ? '-1' : '1',
    amount: null,
    paymentType: PaymentType.VISA,
    referenceId: null,
    date: new Date().toISOString(),
    allocationMode: 'auto',
    counterpartyType: CounterpartyType.CUSTOMER,
  };
}

const directions = ref([
  {
    label: t('payment'),
    value: '1',
  },
  {
    label: t('credit'),
    value: '-1',
  },
]);
</script>

<i18n lang="json">
{
  "en": {
    "invoice": "Invoice",
    "amount": "Amount",
    "paymentType": "Payment Type",
    "direction": "Direction",
    "savePayment": "Save Payment",
    "paymentDate": "Payment Date",
    "payment": "Payment",
    "credit": "Credit/Repayment",
    "noInvoice": "- No Invoice selected -",
    "referenceId": "Referenz ID",
    "allocationMode": "Allocation",
    "dontAutoAllocate": "Don't auto allocate",
    "allocateAll": "Auto allocate to open entries",
    "selectManual": "Select manually"
  },
  "de": {
    "invoice": "Rechnung",
    "amount": "Betrag",
    "paymentType": "Zahlungsart",
    "direction": "Richtung",
    "savePayment": "Zahlung speichern",
    "paymentDate": "Zahlungsdatum",
    "payment": "Zahlung",
    "credit": "Gutschrift/Rückzahlung",
    "noInvoice": "- Keine Rechnung ausgewählt -",
    "referenceId": "Referenz ID",
    "allocationMode": "Zuordnung",
    "dontAutoAllocate": "Nicht automatisch zuordnen",
    "allocateAll": "Automatisch offenen Posten zuordnen",
    "selectManual": "Selbst auswählen"
  },
  "es": {
    "invoice": "Factura",
    "amount": "Importe",
    "paymentType": "Tipo de pago",
    "direction": "Dirección",
    "savePayment": "Guardar pago",
    "paymentDate": "Fecha de pago",
    "payment": "Pago",
    "credit": "Abono / reembolso",
    "noInvoice": "- Ninguna factura seleccionada -",
    "referenceId": "ID de referencia",
    "allocationMode": "Imputación",
    "dontAutoAllocate": "No imputar automáticamente",
    "allocateAll": "Imputar automáticamente a partidas abiertas",
    "selectManual": "Seleccionar manualmente"
  }
}
</i18n>
